import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency } from './util';

// `vendors` (optional): the full list of VendorRegistration records, e.g.
// [{ id, legalVendorName, ... }, ...]. Used to resolve item.legalVendorId ->
// the vendor's legal name. If not supplied, or if an item has no
// legalVendorId / no match is found, we fall back to the old raw
// item.vendorName so legacy cost sheets still print correctly.
export const generateCostSheetPdf = (itemData, vendors = []) => {
  if (!itemData) {
    console.error("No item data provided for PDF generation.");
    return;
  }

  const vendorNameById = new Map(
    (vendors || [])
      .filter(v => v && v.id != null)
      .map(v => [String(v.id), v.legalVendorName])
  );

  const resolveVendorName = (item) => {
    if (item?.legalVendorId != null) {
      const name = vendorNameById.get(String(item.legalVendorId));
      if (name) return name;
    }
    return item?.vendorName || 'N/A';
  };

  const doc = new jsPDF('landscape', 'pt', 'a4');

  const primaryColor = '#007bff';
  const textColor = '#333333';
  const headerBgColor = '#e9ecef';

  let yPos = 40;
  const margin = 40;

  doc.setFontSize(18);
  doc.setTextColor(primaryColor);
  doc.setFont(undefined, 'bold');
  doc.text("COST SHEET", doc.internal.pageSize.width / 2, yPos, { align: 'center' });
  yPos += 30;

  doc.setFontSize(9);
  doc.setTextColor(textColor);
  doc.setFont(undefined, 'normal');

  // Define header data explicitly as a 4-column array for autoTable's body
  const headerTableBody = [
    ["Date:", itemData.date ? new Date(itemData.date).toLocaleDateString('en-GB') : 'N/A', "Delivery Date:", itemData.deliveryDate ? new Date(itemData.deliveryDate).toLocaleDateString('en-GB') : 'N/A'],
    ["Created By:", itemData.createdBy || 'N/A', "Email:", itemData.userEmail || 'N/A'],
    ["Cost Sheet for:", itemData.costsheetfor || 'N/A', "Order Status:", itemData.orderStatus || 'N/A'],
    ["Client Name:", itemData.clientName || 'N/A', "Sales Order No:", itemData.salesOrderNo || 'N/A'],
    ["Sales SPOC:", itemData.salesPOC || 'N/A', "SCM SPOC:", itemData.scmspoc || 'N/A'],
    ["Client Payment Terms:", itemData.clientPaymentTerms || 'N/A', "Location:", itemData.location || 'N/A'],
    // Corrected way to handle colSpan for "Approval Status" and "Cost Sheet Remarks"
    // The content for the merged cells must be in the first element of the row array,
    // and the didParseCell hook should then set the colSpan for that specific cell.
    [
      { content: `Approval Status: ${(() => {
        if (itemData.pending === undefined || itemData.pending === null || itemData.pending === '') {
          return 'Pending';
        } else if (itemData.pending === 'true' || itemData.pending === true) {
          const approvedNames = Array.isArray(itemData.approvedBy) && itemData.approvedBy.length > 0 ? itemData.approvedBy.join(', ') : 'N/A';
          return `Approved By ${approvedNames}`;
        } else if (itemData.pending === 'false' || itemData.pending === false) {
          const rejectedNames = Array.isArray(itemData.approvedBy) ? itemData.approvedBy.filter(Boolean) : [itemData.approvedBy].filter(Boolean);
          const rejectedByText = rejectedNames.length > 0 ? rejectedNames.join(', '): 'N/A';
          return `Rejected By ${rejectedByText} (Note: ${itemData.remarks || 'N/A'})`;
        }
        return 'Pending';
      })()}`, colSpan: 4, styles: { fontStyle: 'bold', halign: 'left' } }
    ],
    [
      { content: `Cost Sheet Remarks: ${itemData.costSheetRemarks || 'N/A'}`, colSpan: 4, styles: { fontStyle: 'bold', halign: 'left' } }
    ]
  ];

  // Draw the header table
  try {
    autoTable(doc, {
      body: headerTableBody,
      startY: yPos,
      margin: { left: margin, right: margin },
      theme: 'plain',
      styles: {
        fontSize: 9,
        textColor: textColor,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'top'
      },
      // You can remove columnStyles for columns 1, 2, 3 as they are now merged by colSpan
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80, halign: 'left' },
      },
      didParseCell: function(data) {
        // No additional didParseCell logic needed here for colSpan as it's defined directly in headerTableBody
        // Keep existing logic for other columns if any, but ensure it doesn't conflict.
        if (data.section === 'body') {
          // General bolding for first and third columns in non-spanning rows
          if (data.row.index < 6 && (data.column.index === 0 || data.column.index === 2)) {
            data.cell.styles.fontStyle = 'bold';
          }
          // The colSpan and content are now defined directly in headerTableBody
          // so this specific `if (data.row.index === 6 || data.row.index === 7)` block might not be strictly necessary here
          // if the cell objects are structured as { content: ..., colSpan: ... }
        }
      },
      // The didDrawCell hook for clearing content is no longer needed with the corrected headerTableBody structure.
    });
    if (doc.autoTable && doc.autoTable.previous && typeof doc.autoTable.previous.finalY === 'number') {
        yPos = doc.autoTable.previous.finalY + 25;
    } else {
        yPos += 150;
    }

  } catch (headerTableError) {
    console.error("Error drawing header autoTable:", headerTableError);
    yPos += 150;
    doc.setFontSize(10);
    doc.setTextColor('red');
    doc.text("Header information could not be rendered due to an error.", margin, yPos - 10);
    doc.setTextColor(textColor);
  }


  doc.setFontSize(12);
  doc.setTextColor(textColor);
  doc.setFont(undefined, 'bold');
  doc.text("Order Details", margin, yPos);
  yPos += 10;

  const tableColumns = [
    { header: "Details", dataKey: "details" },
    { header: "Qty", dataKey: "qty" },
    { header: "GST %", dataKey: "gst" },
    { header: "PP (excl GST)", dataKey: "pp" },
    // { header: "PP (incl GST)", dataKey: "nlc" },
    { header: "SP (excl GST)", dataKey: "sp" },
    // { header: "SP (incl GST)", dataKey: "nsp" },
    { header: "Total PP (Excl GST)", dataKey: "totalPp" },
    { header: "Total SP (Excl GST)", dataKey: "totalSp" },
    { header: "Margin", dataKey: "margin" },
    { header: "Margin %", dataKey: "marginPercent" },
    { header: "Total PP (Incl GST)", dataKey: "totalPpInclGST" },
    { header: "Total SP (Incl GST)", dataKey: "totalSpInclGST" },
    { header: "Vendor", dataKey: "vendorName" },
    { header: "Vendor Payment Terms", dataKey: "vendorPaymentTerms" },
  ];

  const tableRows = (itemData.items|| []).map(item => ({
    details: item.details || 'N/A',
    customDetails: item.customDetails?.trim() || '', // not a column: drawn under details (see didDrawCell)
    qty: item.qty || 0,
    gst: `${item.gst || 0} %`,
    pp: formatCurrency(item.pp || 0),
    nlc: formatCurrency((item.pp || 0) * (1 + (item.gst || 0) / 100)),
    sp: formatCurrency(item.sp || 0),
    nsp: formatCurrency((item.sp || 0) * (1 + (item.gst || 0) / 100)),
    totalPp: formatCurrency((item.qty || 0) * (item.pp || 0)),
    totalSp: formatCurrency((item.qty || 0) * (item.sp || 0)),
    totalPpInclGST: formatCurrency(
      (item.qty || 0) *
      ((item.pp || 0) * (1 + (item.gst || 0) / 100))
    ),
    totalSpInclGST: formatCurrency(
      (item.qty || 0) *
      ((item.sp || 0) * (1 + (item.gst || 0) / 100))
    ),

    margin: formatCurrency(
      ((item.qty || 0) * (item.sp || 0)) -
      ((item.qty || 0) * (item.pp || 0))
    ),

    marginPercent: (() => {
      const itemTotalPp = (item.qty || 0) * (item.pp || 0);
      const itemTotalSp = (item.qty || 0) * (item.sp || 0);
      const itemMargin = itemTotalSp - itemTotalPp;
      return itemTotalPp !== 0 && !isNaN(itemMargin) && isFinite(itemMargin)
        ? `${(itemMargin / itemTotalPp * 100).toFixed(1)}` : '0.0';
    })() + ' %',
    vendorName: resolveVendorName(item),
    vendorPaymentTerms: item.vendorPaymentTerms || 'N/A',
  }));

  
  const transportationTotalPp = parseFloat(itemData.transportationTotalPp || 0);
  const transportationTotalSp = parseFloat(itemData.transportationTotalSp || 0);
  const customizationTotalPp = parseFloat(itemData.customizationTotalPp || 0);
  const customizationTotalSp = parseFloat(itemData.customizationTotalSp || 0);
  const marketingTotalPp = parseFloat(itemData.marketingTotalPp || 0);
  const marketingTotalSp = parseFloat(itemData.marketingTotalSp || 0);

  const tGst = parseFloat(itemData?.costSummary?.transportationGST || 0);
  const cGst = parseFloat(itemData?.costSummary?.customizationGST || 0);
  const mGst = parseFloat(itemData?.costSummary?.marketingGST || 0);

  const tPpInclGST = transportationTotalPp * (1 + tGst / 100);
  const cPpInclGST = customizationTotalPp * (1 + cGst / 100);
  const mPpInclGST = marketingTotalPp * (1 + mGst / 100);

  const tSpInclGST = transportationTotalSp * (1 + tGst / 100);
  const cSpInclGST = customizationTotalSp * (1 + cGst / 100);
  const mSpInclGST = marketingTotalSp * (1 + mGst / 100);

  const totalMarginTransportation = transportationTotalSp - transportationTotalPp;
  const totalMarginCustomization = customizationTotalSp - customizationTotalPp;
  const totalMarginMarketing = marketingTotalSp - marketingTotalPp;

  const customRowsData = itemData.customRows || [];

  customRowsData.forEach(row => {
  const cpPp  = parseFloat(row.customTotalPp || 0);
  const cpSp  = parseFloat(row.customTotalSp || 0);
  const cpGst = parseFloat(row.customGST || 0);

    tableRows.push({
      details: row.customLabel || 'Custom',
      qty: '', gst: `${cpGst} %`, pp: '', nlc: '', sp: '', nsp: '',
      totalPp: formatCurrency(cpPp),
      totalSp: formatCurrency(cpSp),
      totalPpInclGST: formatCurrency( cpPp * (1 + cpGst / 100) ),
      totalSpInclGST: formatCurrency( cpSp * (1 + cpGst / 100) ),
      margin: formatCurrency(cpSp - cpPp),
      marginPercent: `${( cpPp !== 0 ? ((cpSp - cpPp) / cpPp) * 100 : 0 ).toFixed(1)} %`,
      vendorName: '',
      vendorPaymentTerms: '',
    });
  });
  tableRows.push({
    details: 'Transportation',
    qty: '', gst: `${tGst} %`, pp: '', nlc: '', sp: '', nsp: '',
    totalPp: formatCurrency(transportationTotalPp),
    totalSp: formatCurrency(transportationTotalSp),
    totalPpInclGST: formatCurrency(tPpInclGST),
    totalSpInclGST: formatCurrency(tSpInclGST),
    margin: formatCurrency(totalMarginTransportation),
    marginPercent: `${(transportationTotalPp !== 0 ? (totalMarginTransportation / transportationTotalPp) * 100 : 0).toFixed(1)} %`,
    vendorName: '', vendorPaymentTerms: '',
  });

  tableRows.push({
    details: 'Customization',
    qty: '', gst: `${cGst} %`, pp: '', nlc: '', sp: '', nsp: '',
    totalPp: formatCurrency(customizationTotalPp),
    totalSp: formatCurrency(customizationTotalSp),
    totalPpInclGST: formatCurrency(cPpInclGST),
    totalSpInclGST: formatCurrency(cSpInclGST),
    margin: formatCurrency(totalMarginCustomization),
    marginPercent: `${(customizationTotalPp !== 0 ? (totalMarginCustomization / customizationTotalPp) * 100 : 0).toFixed(1)} %`,
    vendorName: '', vendorPaymentTerms: '',
  });

  tableRows.push({
    details: 'Marketing',
    qty: '', gst: `${mGst} %`, pp: '', nlc: '', sp: '', nsp: '',
    totalPp: formatCurrency(marketingTotalPp),
    totalSp: formatCurrency(marketingTotalSp),
    totalPpInclGST: formatCurrency(mPpInclGST),
    totalSpInclGST: formatCurrency(mSpInclGST),
    margin: formatCurrency(totalMarginMarketing),
    marginPercent: `${(marketingTotalPp !== 0 ? (totalMarginMarketing / marketingTotalPp) * 100 : 0).toFixed(1)} %`,
    vendorName: '', vendorPaymentTerms: '',
  });

  const grandTotalSPExcl = (
    itemData.items?.reduce((total, item) =>
      total +
      ((item.qty || 0) * (item.sp || 0)), 0
    ) || 0
  ) +
    customRowsData.reduce((total, row) =>
      total + Number(row.customTotalSp || 0), 0
    ) +
    transportationTotalSp +
    customizationTotalSp +
    marketingTotalSp;

  const grandTotalPPExcl = (
    itemData.items?.reduce((total, item) =>
      total +
      ((item.qty || 0) * (item.pp || 0)), 0
    ) || 0
  ) +
    customRowsData.reduce((total, row) =>
      total + Number(row.customTotalPp || 0), 0
    ) +
    transportationTotalPp +
    customizationTotalPp +
    marketingTotalPp;

  const grandTotalPP = (
    itemData.items?.reduce((total, item) =>
      total +
      (
        (item.qty || 0) *
        ((item.pp || 0) * (1 + (item.gst || 0) / 100))
      ), 0
    ) || 0
  ) +
    customRowsData.reduce((total, row) =>
      total +
      (
        Number(row.customTotalPp || 0) *
        (1 + (row.customGST || 0) / 100)
      ), 0
    ) +
    tPpInclGST +
    cPpInclGST +
    mPpInclGST;

  const grandTotalSP = (
    itemData.items?.reduce((total, item) =>
      total +
      (
        (item.qty || 0) *
        ((item.sp || 0) * (1 + (item.gst || 0) / 100))
      ), 0
    ) || 0
  ) +
    customRowsData.reduce((total, row) =>
      total +
      (
        Number(row.customTotalSp || 0) *
        (1 + (row.customGST || 0) / 100)
      ), 0
    ) +
    tSpInclGST +
    cSpInclGST +
    mSpInclGST;

  const grandMargin =
    grandTotalSPExcl - grandTotalPPExcl;

  const grandMarginPercent =
    grandTotalPPExcl !== 0
      ? (grandMargin / grandTotalPPExcl) * 100
      : 0;

  tableRows.push({
    details: 'Grand-Total',
    qty: '', gst: '', pp: '', nlc: '', sp: '', nsp: '',
    totalPp: formatCurrency(grandTotalPPExcl),
    totalSp: formatCurrency(grandTotalSPExcl),
    totalPpInclGST: formatCurrency(grandTotalPP),
    totalSpInclGST: formatCurrency(grandTotalSP),
    margin: formatCurrency(grandMargin),
    marginPercent: `${grandMarginPercent.toFixed(1)} %`,
    vendorName: '',
    vendorPaymentTerms: '',
  });


  autoTable(doc, {
    columns: tableColumns,
    body: tableRows,
    startY: yPos,
    theme: 'grid',
    styles: {
      fontSize: 6,
      cellPadding: 2,
      textColor: textColor,
      lineColor: '#e0e0e0',
      lineWidth: 0.5,
      overflow: 'linebreak',
      cellWidth: 'auto',
    },
    headStyles: {
      fillColor: [parseInt(headerBgColor.substring(1,3), 16), parseInt(headerBgColor.substring(3,5), 16), parseInt(headerBgColor.substring(5,7), 16)],
      textColor: [0, 0, 0],
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      details: { cellWidth: 90, halign: 'left' },
      qty: { cellWidth: 30, halign: 'center' },
      gst: { cellWidth: 35, halign: 'center' },
      pp: { cellWidth: 55, halign: 'right' },
      nlc: { cellWidth: 55, halign: 'right' },
      sp: { cellWidth: 55, halign: 'right' },
      nsp: { cellWidth: 55, halign: 'right' },
      totalPp: { cellWidth: 65, halign: 'right' },
      totalSp: { cellWidth: 65, halign: 'right' },
      totalPpInclGST: { cellWidth: 65, halign: 'right' },
      totalSpInclGST: { cellWidth: 65, halign: 'right' },
      margin: { cellWidth: 50, halign: 'right' },
      marginPercent: { cellWidth: 50, halign: 'right' },
      vendorName: { cellWidth: 70, halign: 'left' },
      vendorPaymentTerms: { cellWidth: 70, halign: 'left' },
    },
    didParseCell: function(data) {
        if (data.row.raw.details === 'Grand-Total') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'left';
        }
        // Custom details go under the product name, highlighted as on screen: reserve blank
        // lines for them here (so the row grows to fit) and draw them in didDrawCell
        const custom = data.row.raw.customDetails;
        if (data.section === 'body' && data.column.dataKey === 'details' && custom) {
            const fontSize = data.cell.styles.fontSize;
            const width = 90 - 4; // the details column's cellWidth minus its padding
            const nameLines = doc.splitTextToSize(String(data.row.raw.details), width, { fontSize });
            const customLines = doc.splitTextToSize(custom, width - 4, { fontSize });
            data.cell.text = [...nameLines, ...customLines.map(() => ' ')];
            data.cell.customDetails = { start: nameLines.length, lines: customLines };
        }
    },
    didDrawCell: function(data) {
        const custom = data.cell.customDetails;
        if (data.section !== 'body' || !custom) return;
        const fontSize = data.cell.styles.fontSize;
        const lineHeight = fontSize * doc.getLineHeightFactor();
        const x = data.cell.x + data.cell.padding('left');
        const y = data.cell.y + data.cell.padding('top') + custom.start * lineHeight;
        const width = data.cell.width - data.cell.padding('left') - data.cell.padding('right');
        doc.setFillColor(254, 249, 195); // light yellow
        doc.setDrawColor(253, 230, 138);
        doc.setLineWidth(0.5);
        doc.rect(x, y, width, custom.lines.length * lineHeight + 1, 'FD');
        doc.setFontSize(fontSize);
        doc.setTextColor(113, 63, 18);
        doc.text(custom.lines, x + 2, y + 0.5, { baseline: 'top' });
    },
    didDrawPage: function(data) {
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text("Page " + data.pageNumber, doc.internal.pageSize.width - margin, doc.internal.pageSize.height - 20, { align: 'right' });
    }
  });

  const sanitize = (str) =>
  String(str || 'N_A').replace(/[<>:"/\\|?*]+/g, '_');
  const filename = `cosheet_${sanitize(itemData.salesOrderNo)}_${sanitize(itemData.costsheetfor)}.pdf`;
  doc.save(filename);
};